import { useCallback, useMemo, useState } from "react";
import { Button, Stack } from "@mui/material";
import { useForm } from "react-final-form";

import { useTranslation } from "@/lib/i18n";
import MapLayerConfiguration from "@/features/admin/components/map-layer-configuration/MapLayerConfiguration";
import { MapWidgetConfigurationAllOfLayers } from "@/lib/api";
import { LayerTypes } from "@/types/map";
import { BasicWidgetFormProps } from "@/features/widget-forms/dialogs/widget-dialog-types";

const DEFAULT_VALUES = {
  layers: [] as Array<MapWidgetConfigurationAllOfLayers>,
};

const DEFAULT_LAYER_VALUES = {
  id: "",
  type: "static",
  title: "",
  enabled_by_default: true,
};

export function EarlyWarningTableWidgetForm({ values }: BasicWidgetFormProps) {
  const { t } = useTranslation();
  const [initialValues] = useState(() => {
    const configuration = values?.configuration;
    if (!configuration) return DEFAULT_VALUES;

    const initialValues = DEFAULT_VALUES;

    if ("layers" in configuration) {
      initialValues.layers = configuration.layers;
    }

    return initialValues;
  });

  const form = useForm();
  const formValues = form.getState().values;
  const layers = formValues?.configuration?.layers;
  const layerCount = useMemo(() => {
    if (!layers) return 0;
    return layers.length;
  }, [layers]);

  const removeLayer = useCallback(
    (id: number) => {
      if (layers) {
        const newLayers = [...layers];
        newLayers.splice(id, 1);
        form.change("configuration.layers", newLayers);
      }
    },
    [layers],
  );

  const addLayer = useCallback(() => {
    form.change(`configuration.layers[${layerCount}]`, {});
  }, [layerCount]);

  return (
    <Stack direction="column" gap={2} mt={2}>
      {[...Array(layerCount).keys()].map((index) => (
        <MapLayerConfiguration
          index={index}
          key={`layer${index}`}
          name={"configuration.layers"}
          label={t("admin.widgets.fields.map.type.label")}
          initialValue={initialValues["layers"][index] || DEFAULT_LAYER_VALUES}
          removeLayer={() => removeLayer(index)}
          layers={layers}
          allowedLayerTypes={[LayerTypes.STATIC]}
        />
      ))}

      <Button onClick={addLayer}>{t("admin.widgets.fields.map.layer.new")}</Button>
    </Stack>
  );
}

export default EarlyWarningTableWidgetForm;
